/**
 * SecureFileStatusCard — SDE Phase 2 (s88).
 *
 * Planner-dashboard indicator: once the Secure File is set up, shows where it's stored.
 * Reads `secureFileSetupAt` from the client record and the `secure_file_location` value
 * (surfaced through the Where-Is-Everything aggregate, which already includes the
 * aggregate_role='location' field). Renders nothing until setup is done.
 */
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ShieldCheck, FolderOpen } from "lucide-react";
import type { Client } from "@shared/schema";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { useSecureFileOpen } from "@/components/SecureFileOpenProvider";
import { SAGE, WARM } from "@shared/brand";

export function SecureFileStatusCard({ clientData }: { clientData: Client }) {
  const setupComplete = !!clientData.secureFileSetupAt;

  const { data: wie = [] } = useQuery<Array<{ fieldKey?: string | null; location?: string }>>({
    queryKey: ["/api/clients", clientData.id, CATEGORY_SLUGS.WHERE_IS_EVERYTHING],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientData.id}/where-is-everything`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientData.id,
  });

  const location = wie.find((r) => r.fieldKey === "secure_file_location")?.location?.trim();
  const { openFile } = useSecureFileOpen();
  const [, setLocation] = useLocation();

  if (!setupComplete && !location) return null;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm"
      style={{ borderColor: WARM.stone, backgroundColor: SAGE[50] }}
      data-testid="secure-file-status"
    >
      <span className="flex items-center gap-2.5 text-foreground">
        <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: SAGE[600] }} />
        <span>
          <span className="font-medium">Your secure file</span>
          {location ? <> is stored at <span className="font-medium">{location}</span>.</> : <> is set up.</>}
        </span>
      </span>
      <span className="flex items-center gap-3">
        <button
          type="button"
          onClick={openFile}
          data-testid="secure-file-open-button"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-white"
          style={{ borderColor: SAGE[600], color: SAGE[600] }}
        >
          <FolderOpen className="h-3.5 w-3.5" />
          Open file
        </button>
        <button
          type="button"
          onClick={() => setLocation("/settings#secure-file")}
          data-testid="secure-file-manage-link"
          className="whitespace-nowrap text-sm font-medium underline underline-offset-2 transition-colors hover:opacity-80"
          style={{ color: SAGE[600] }}
        >
          Manage
        </button>
      </span>
    </div>
  );
}
